import { useState, useEffect, useCallback } from 'react';

// Custom hook for API calls with loading, error, and data states
export const useApi = (apiFunction, dependencies = []) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const execute = useCallback(async (...params) => {
    setLoading(true);
    setError(null);
    try {
      const result = await apiFunction(...params);
      if (result.success) {
        setData(result.data);
        return result;
      } else {
        setError(result.error);
        return result;
      }
    } catch (err) {
      const errorMessage = err.message || 'An unexpected error occurred';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, dependencies);

  const reset = () => {
    setData(null);
    setError(null);
    setLoading(false);
  };

  return { data, loading, error, execute, reset };
};

// Custom hook for paginated API calls
export const usePaginatedApi = (apiFunction, initialParams = {}) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [limit, setLimit] = useState(initialParams.limit || 10);

  const fetchData = useCallback(async (pageNum = page, customParams = {}) => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        ...initialParams,
        ...customParams,
        page: pageNum,
        limit
      };
      const result = await apiFunction(params);
      if (result.success) {
        setData(result.data);
        setTotal(result.total || 0);
        setTotalPages(Math.ceil((result.total || 0) / limit));
        setPage(pageNum);
        return result;
      } else {
        setError(result.error);
        return result;
      }
    } catch (err) {
      const errorMessage = err.message || 'An unexpected error occurred';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, [apiFunction, page, limit, initialParams]);

  const nextPage = () => {
    if (page < totalPages) {
      fetchData(page + 1);
    }
  };

  const previousPage = () => {
    if (page > 1) {
      fetchData(page - 1);
    }
  };

  const goToPage = (pageNum) => {
    if (pageNum >= 1 && pageNum <= totalPages) {
      fetchData(pageNum);
    }
  };

  const changeLimit = (newLimit) => {
    setLimit(newLimit);
    fetchData(1);
  };

  const refresh = () => {
    fetchData(page);
  };

  useEffect(() => {
    fetchData(1);
  }, []);

  return {
    data,
    loading,
    error,
    page,
    totalPages,
    total,
    limit,
    fetchData,
    nextPage,
    previousPage,
    goToPage,
    changeLimit,
    refresh
  };
};

// Custom hook for form submissions
export const useFormApi = (apiFunction) => {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const submit = useCallback(async (formData) => {
    setSubmitting(true);
    setError(null);
    setSuccess(false);
    try {
      const result = await apiFunction(formData);
      if (result.success) {
        setSuccess(true);
        return result;
      } else {
        setError(result.error);
        return result;
      }
    } catch (err) {
      const errorMessage = err.message || 'An unexpected error occurred';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setSubmitting(false);
    }
  }, [apiFunction]);

  const reset = () => {
    setError(null);
    setSuccess(false);
    setSubmitting(false);
  };

  return { submit, submitting, error, success, reset };
};

// Custom hook for real-time data updates
export const useRealtimeApi = (apiFunction, interval = 30000, dependencies = []) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  const fetchData = useCallback(async (...params) => {
    setLoading(true);
    setError(null);
    try {
      const result = await apiFunction(...params);
      if (result.success) {
        setData(result.data);
        setLastUpdated(new Date());
        return result;
      } else {
        setError(result.error);
        return result;
      }
    } catch (err) {
      const errorMessage = err.message || 'An unexpected error occurred';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, dependencies);

  useEffect(() => {
    fetchData();
    const intervalId = setInterval(fetchData, interval);
    return () => clearInterval(intervalId);
  }, [fetchData, interval]);

  const refresh = () => {
    fetchData();
  };

  return { data, loading, error, lastUpdated, refresh };
};

// Custom hook for search functionality
export const useSearch = (apiFunction, debounceTime = 500) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [timeoutId, setTimeoutId] = useState(null);

  const search = useCallback((searchQuery) => {
    setQuery(searchQuery);

    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    if (!searchQuery || searchQuery.trim() === '') {
      setResults([]);
      return;
    }

    const newTimeoutId = setTimeout(async () => {
      setLoading(true);
      setError(null);
      try {
        const result = await apiFunction({ query: searchQuery });
        if (result.success) {
          setResults(result.data);
        } else {
          setError(result.error);
          setResults([]);
        }
      } catch (err) {
        const errorMessage = err.message || 'Search failed';
        setError(errorMessage);
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, debounceTime);

    setTimeoutId(newTimeoutId);
  }, [apiFunction, debounceTime, timeoutId]);

  const clear = () => {
    setQuery('');
    setResults([]);
    setError(null);
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
  };

  return { query, results, loading, error, search, clear };
};

// Custom hook for file upload
export const useFileUpload = (apiFunction) => {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState(null);
  const [uploadedFile, setUploadedFile] = useState(null);

  const upload = useCallback(async (file, onProgress) => {
    setUploading(true);
    setError(null);
    setProgress(0);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const config = {
        onUploadProgress: (progressEvent) => {
          const percentComplete = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          setProgress(percentComplete);
          if (onProgress) {
            onProgress(percentComplete);
          }
        }
      };

      const result = await apiFunction(formData, config);
      if (result.success) {
        setUploadedFile(result.data);
        return result;
      } else {
        setError(result.error);
        return result;
      }
    } catch (err) {
      const errorMessage = err.message || 'Upload failed';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setUploading(false);
    }
  }, [apiFunction]);

  const reset = () => {
    setError(null);
    setProgress(0);
    setUploadedFile(null);
    setUploading(false);
  };

  return { upload, uploading, progress, error, uploadedFile, reset };
};